import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useLessonsByModule } from '@/hooks/useLessons'
import { useEnrollmentProgress } from '@/hooks/useProgress'
import type { Lesson } from '@/types/lesson'

interface ModuleSyllabusProps {
    moduleId: string
    courseId: string
    enrollmentId?: string
    isEnrolled: boolean
    isPaidCourse: boolean
}

export const ModuleSyllabus: React.FC<ModuleSyllabusProps> = ({
    moduleId,
    courseId,
    enrollmentId,
    isEnrolled,
    isPaidCourse,
}) => {
    const { data: lessons, isLoading } = useLessonsByModule(moduleId)
    const { data: progress } = useEnrollmentProgress(enrollmentId || '')

    const completedLessonIds = useMemo(() => {
        if (!progress) return new Set<string>()
        return new Set(
            progress
                .filter((p) => p.completed)
                .map((p) => p.lessonId)
        )
    }, [progress])

    const canAccessLesson = (lesson: Lesson, index: number) => {
        // If not enrolled, only previewable lessons
        if (!isEnrolled) return lesson.isPreviewable

        // If enrolled:
        // 1. First lesson is always accessible
        if (index === 0) return true

        // 2. Subsequent lessons: check if PREVIOUS lesson is completed
        const previousLesson = lessons?.[index - 1]
        return previousLesson ? completedLessonIds.has(previousLesson.id) : false
    }

    const getContentTypeIcon = (contentType: string) => {
        switch (contentType) {
            case 'video':
                return 'play_circle'
            case 'pdf':
                return 'description'
            case 'text':
                return 'article'
            default:
                return 'school'
        }
    }

    if (isLoading) {
        return (
            <div className="space-y-2 py-2">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="h-12 bg-gray-200 dark:bg-gray-800 rounded-lg animate-pulse" />
                ))}
            </div>
        )
    }

    if (!lessons || lessons.length === 0) {
        return (
            <div className="py-4 text-center text-sm text-gray-500 dark:text-text-muted">
                No lessons available
            </div>
        )
    }

    // Count locked lessons (paid course only)
    const lockedCount = isPaidCourse && !isEnrolled
        ? lessons.filter((l) => !l.isPreviewable).length
        : 0

    // Count total lessons for free courses where user hasn't enrolled
    const needsEnroll = !isEnrolled

    return (
        <div className="space-y-1 pt-2">
            {lessons.map((lesson, index) => {
                const canAccess = canAccessLesson(lesson, index)
                const isLocked = (isPaidCourse && !isEnrolled && !lesson.isPreviewable) || (isEnrolled && !canAccess)
                const isCompleted = completedLessonIds.has(lesson.id)

                // Determine badge type
                let badgeType: 'free_preview' | 'locked' | 'enrolled' | 'free_start' | null = null
                if (isEnrolled) {
                    badgeType = null // enrolled users don't need badges
                } else if (isPaidCourse) {
                    badgeType = lesson.isPreviewable ? 'free_preview' : 'locked'
                } else {
                    // Free course, not enrolled yet
                    badgeType = lesson.isPreviewable ? 'free_preview' : 'free_start'
                }

                const lessonContent = (
                    <div
                        className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                            canAccess
                                ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5'
                                : 'cursor-not-allowed'
                        } ${badgeType === 'free_preview' ? 'bg-emerald-50/50 dark:bg-emerald-500/5' : ''}`}
                    >
                        {/* Order Number */}
                        <span className="w-6 text-center text-xs font-bold text-slate-400">
                            {index + 1}
                        </span>

                        {/* Icon */}
                        <div className={`size-8 rounded-lg flex items-center justify-center shrink-0 ${
                            isLocked
                                ? 'bg-slate-100 dark:bg-white/5'
                                : badgeType === 'free_preview'
                                    ? 'bg-emerald-100 dark:bg-emerald-500/10'
                                    : isCompleted
                                        ? 'bg-green-100 dark:bg-green-500/10'
                                        : 'bg-primary/10'
                        }`}>
                            <span className={`material-symbols-outlined text-[18px] ${
                                isLocked
                                    ? 'text-slate-400 dark:text-slate-500'
                                    : badgeType === 'free_preview'
                                        ? 'text-emerald-600 dark:text-emerald-400'
                                        : isCompleted
                                            ? 'text-green-500'
                                            : 'text-primary'
                            }`}>
                                {isLocked ? 'lock' : isCompleted ? 'check_circle' : getContentTypeIcon(lesson.contentType)}
                            </span>
                        </div>

                        {/* Title */}
                        <div className="flex-1 min-w-0">
                            <span className={`text-sm font-medium block truncate ${
                                isLocked ? 'text-slate-400 dark:text-slate-500' : 'text-slate-900 dark:text-white'
                            }`}>
                                {lesson.title}
                            </span>
                        </div>

                        {/* Badges */}
                        <div className="flex items-center gap-2 shrink-0">
                            {/* Free Preview Badge — shown on previewable lessons for non-enrolled users */}
                            {badgeType === 'free_preview' && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-lg border border-emerald-200 dark:border-emerald-500/20">
                                    <span className="material-symbols-outlined text-[12px]">visibility</span>
                                    Free Preview
                                </span>
                            )}

                            {/* Locked / Premium Badge — paid course, not previewable */}
                            {badgeType === 'locked' && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-lg border border-amber-200 dark:border-amber-500/20">
                                    <span className="material-symbols-outlined text-[12px]">lock</span>
                                    Beli Kursus
                                </span>
                            )}

                            {/* Free course but needs enrollment */}
                            {badgeType === 'free_start' && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-lg border border-blue-200 dark:border-blue-500/20">
                                    <span className="material-symbols-outlined text-[12px]">play_arrow</span>
                                    Mulai Kursus
                                </span>
                            )}

                            {/* Completion Checkmark */}
                            {isCompleted && (
                                <span className="material-symbols-outlined text-lg text-green-500">
                                    check_circle
                                </span>
                            )}

                            {/* Duration */}
                            {lesson.durationMinutes > 0 && (
                                <span className="text-[11px] font-medium text-slate-400 tabular-nums">
                                    {lesson.durationMinutes} min
                                </span>
                            )}
                        </div>
                    </div>
                )

                if (canAccess) {
                    return (
                        <Link
                            key={lesson.id}
                            to={`/courses/${courseId}/lessons/${lesson.id}`}
                            className="block"
                        >
                            {lessonContent}
                        </Link>
                    )
                }

                return (
                    <div key={lesson.id}>
                        {lessonContent}
                    </div>
                )
            })}

            {/* CTA Banner for paid courses with locked lessons */}
            {lockedCount > 0 && (
                <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 via-amber-50/50 to-amber-50 dark:from-amber-500/5 dark:via-amber-500/10 dark:to-amber-500/5 rounded-xl border border-amber-200/50 dark:border-amber-500/10">
                    <div className="flex items-center gap-3">
                        <div className="size-10 rounded-xl bg-amber-100 dark:bg-amber-500/10 flex items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-amber-600 dark:text-amber-400 text-[20px]">shopping_cart</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-slate-900 dark:text-white">
                                {lockedCount} materi memerlukan pembelian
                            </p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                Beli kursus ini untuk mengakses semua materi
                            </p>
                        </div>
                        <button
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            className="px-4 py-2 bg-amber-500 text-white text-xs font-bold rounded-lg hover:bg-amber-600 active:scale-95 transition-all shadow-sm shrink-0"
                        >
                            Beli Sekarang
                        </button>
                    </div>
                </div>
            )}

            {/* CTA Banner for free courses — prompt to start */}
            {!isPaidCourse && needsEnroll && (
                <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 via-blue-50/50 to-blue-50 dark:from-blue-500/5 dark:via-blue-500/10 dark:to-blue-500/5 rounded-xl border border-blue-200/50 dark:border-blue-500/10">
                    <div className="flex items-center gap-3">
                        <div className="size-10 rounded-xl bg-blue-100 dark:bg-blue-500/10 flex items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-[20px]">rocket_launch</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-slate-900 dark:text-white">
                                Mulai kursus gratis ini
                            </p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                Daftar untuk mulai belajar semua materi
                            </p>
                        </div>
                        <button
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            className="px-4 py-2 bg-blue-500 text-white text-xs font-bold rounded-lg hover:bg-blue-600 active:scale-95 transition-all shadow-sm shrink-0"
                        >
                            Mulai Sekarang
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ModuleSyllabus
